/**
 * Frontend API client.
 *  - getSessionToken() pulls a fresh App Bridge JWT
 *  - gql(query, variables) hits our /api/graphql proxy
 *  - setup() runs first-time install setup (idempotent)
 *  - all form CRUD functions use Admin GraphQL via gql()
 */

let appBridge = null;
function getAppBridge() {
  if (appBridge) return appBridge;
  const host = new URLSearchParams(window.location.search).get('host');
  const apiKey = window.shopifyApiKey || document.querySelector('meta[name="shopify-api-key"]')?.content;
  if (!window.shopify) throw new Error('Shopify App Bridge global missing');
  appBridge = window.shopify;
  return appBridge;
}

async function getSessionToken() {
  const bridge = getAppBridge();
  return bridge.idToken();
}

export async function gql(query, variables = {}) {
  const token = await getSessionToken();
  const res = await fetch('/api/graphql', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type':  'application/json',
    },
    body: JSON.stringify({ query, variables }),
  });
  const data = await res.json();
  if (data.errors) throw new Error(JSON.stringify(data.errors));
  return data.data;
}

export async function runSetup() {
  const token = await getSessionToken();
  const res = await fetch('/api/setup', {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return res.json();
}

/* ---------- Form template CRUD ---------- */

export async function listForms() {
  const data = await gql(`{
    metaobjects(type: "order_form_template", first: 100) {
      nodes {
        id handle
        title:  field(key: "title")  { value }
        fields: field(key: "fields") { references(first: 50) { nodes { ... on Metaobject { id handle } } } }
      }
    }
  }`);
  return data.metaobjects.nodes.map((n) => ({
    id: n.id,
    handle: n.handle,
    title: n.title?.value || n.handle,
    fieldCount: n.fields?.references?.nodes?.length || 0,
  }));
}

export async function getForm(id) {
  const data = await gql(`
    query($id: ID!) {
      metaobject(id: $id) {
        id handle
        title:  field(key: "title")  { value }
        fields: field(key: "fields") {
          references(first: 50) { nodes { ... on Metaobject {
            id handle
            f_handle: field(key: "handle") { value }
            f_type: field(key: "type") { value }
            f_label: field(key: "label") { value }
            f_required: field(key: "required") { value }
            f_placeholder: field(key: "placeholder") { value }
            f_help: field(key: "help_text") { value }
            f_max: field(key: "max_length") { value }
            f_accept: field(key: "accept") { value }
            f_content: field(key: "content") { value }
            f_confirm: field(key: "confirm_label") { value }
            f_rules: field(key: "rules") { value }
            f_options: field(key: "options") {
              references(first: 50) { nodes { ... on Metaobject {
                id
                o_label: field(key: "label") { value }
                o_value: field(key: "value") { value }
                o_price: field(key: "price") { value }
                o_help:  field(key: "help_text") { value }
              }}}
            }
          }}}
        }
      }
    }
  `, { id });
  const m = data.metaobject;
  return {
    id: m.id,
    handle: m.handle,
    title: m.title?.value || '',
    fields: (m.fields?.references?.nodes || []).map((f) => ({
      id: f.id,
      handle: f.f_handle?.value || '',
      type: f.f_type?.value || 'text',
      label: f.f_label?.value || '',
      required: f.f_required?.value === 'true',
      placeholder: f.f_placeholder?.value || '',
      helpText: f.f_help?.value || '',
      maxLength: parseInt(f.f_max?.value) || null,
      accept: f.f_accept?.value || '',
      content: f.f_content?.value || '',
      confirmLabel: f.f_confirm?.value || '',
      rules: f.f_rules?.value || '',
      options: (f.f_options?.references?.nodes || []).map((o) => ({
        id: o.id,
        label: o.o_label?.value || '',
        value: o.o_value?.value || '',
        price: parseFloat(o.o_price?.value) || 0,
        helpText: o.o_help?.value || '',
      })),
    })),
  };
}

async function upsertOption(opt) {
  if (opt.id) {
    await gql(`
      mutation($id: ID!, $fields: [MetaobjectFieldInput!]!) {
        metaobjectUpdate(id: $id, metaobject: { fields: $fields }) { metaobject { id } userErrors { message } }
      }`, {
      id: opt.id,
      fields: [
        { key: 'label', value: opt.label },
        { key: 'value', value: opt.value || opt.label },
        { key: 'price', value: String(opt.price || 0) },
        { key: 'help_text', value: opt.helpText || '' },
      ],
    });
    return opt.id;
  } else {
    const res = await gql(`
      mutation($metaobject: MetaobjectCreateInput!) {
        metaobjectCreate(metaobject: $metaobject) { metaobject { id } userErrors { message } }
      }`, {
      metaobject: {
        type: 'order_form_option',
        fields: [
          { key: 'label', value: opt.label },
          { key: 'value', value: opt.value || opt.label },
          { key: 'price', value: String(opt.price || 0) },
          { key: 'help_text', value: opt.helpText || '' },
        ],
      },
    });
    return res.metaobjectCreate.metaobject.id;
  }
}

async function upsertField(field) {
  const optionIds = await Promise.all((field.options || []).map(upsertOption));
  const fields = [
    { key: 'handle', value: field.handle },
    { key: 'type', value: field.type },
    { key: 'label', value: field.label },
    { key: 'required', value: field.required ? 'true' : 'false' },
    { key: 'placeholder', value: field.placeholder || '' },
    { key: 'help_text', value: field.helpText || '' },
    { key: 'max_length', value: field.maxLength ? String(field.maxLength) : '' },
    { key: 'accept', value: field.accept || '' },
    { key: 'content', value: field.content || '' },
    { key: 'confirm_label', value: field.confirmLabel || '' },
    { key: 'rules', value: field.rules || '' },
    { key: 'options', value: JSON.stringify(optionIds) },
  ];
  if (field.id) {
    await gql(`
      mutation($id: ID!, $fields: [MetaobjectFieldInput!]!) {
        metaobjectUpdate(id: $id, metaobject: { fields: $fields }) { metaobject { id } userErrors { message } }
      }`, { id: field.id, fields });
    return field.id;
  } else {
    const res = await gql(`
      mutation($metaobject: MetaobjectCreateInput!) {
        metaobjectCreate(metaobject: $metaobject) { metaobject { id } userErrors { message } }
      }`, {
      metaobject: { type: 'order_form_field', fields },
    });
    return res.metaobjectCreate.metaobject.id;
  }
}

export async function saveForm(form) {
  const fieldIds = [];
  for (const field of form.fields) fieldIds.push(await upsertField(field));
  const fields = [
    { key: 'title', value: form.title },
    { key: 'fields', value: JSON.stringify(fieldIds) },
  ];
  if (form.id) {
    await gql(`
      mutation($id: ID!, $fields: [MetaobjectFieldInput!]!) {
        metaobjectUpdate(id: $id, metaobject: { fields: $fields }) { metaobject { id } userErrors { message } }
      }`, { id: form.id, fields });
    return form.id;
  } else {
    const res = await gql(`
      mutation($metaobject: MetaobjectCreateInput!) {
        metaobjectCreate(metaobject: $metaobject) { metaobject { id } userErrors { message } }
      }`, {
      metaobject: { type: 'order_form_template', fields },
    });
    return res.metaobjectCreate.metaobject.id;
  }
}

export async function deleteForm(id) {
  await gql(`
    mutation($id: ID!) {
      metaobjectDelete(id: $id) { deletedId userErrors { message } }
    }`, { id });
}

/* ---------- Product assignment ---------- */

export async function listProducts(after = null) {
  const data = await gql(`
    query($after: String) {
      products(first: 50, after: $after) {
        nodes {
          id title handle
          formMeta: metafield(namespace: "app", key: "order_form") { value }
        }
        pageInfo { hasNextPage endCursor }
      }
    }`, { after });
  return data.products;
}

export async function assignFormToProduct(productId, formTemplateId) {
  return gql(`
    mutation($metafields: [MetafieldsSetInput!]!) {
      metafieldsSet(metafields: $metafields) { userErrors { message field } }
    }`, {
    metafields: [{
      ownerId: productId,
      namespace: 'app',
      key: 'order_form',
      type: 'metaobject_reference',
      value: formTemplateId || '',
    }],
  });
}
